import React, { useState, useEffect } from 'react';
import { Loader2, Plus, Trash2, Edit2, Save, X, Building2, GripVertical, ChevronDown, ChevronUp, Briefcase } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const IndustryMenuPanel = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [activeTab, setActiveTab] = useState('basic'); // 'basic', 'page', 'solutions', 'casestudies'
  const { getAuthHeaders } = useAuth();

  const emptyItem = {
    name: '',
    slug: '',
    icon: 'Building2',
    description: '',
    image: '',
    hero_title: '',
    hero_subtitle: '',
    hero_image: '',
    content: '',
    content_image: '',
    features: [],
    benefits: [],
    sub_items: [],
    case_studies: [],
    order: 0,
    is_active: true
  };

  const emptySubItem = {
    name: '',
    slug: '',
    description: '',
    hero_title: '',
    hero_subtitle: '',
    hero_image: '',
    content: '',
    content_image: '',
    features: [],
    benefits: [],
    order: 0
  };

  const emptyCaseStudy = {
    client: '',
    title: '',
    challenge: '',
    solution: '',
    results: '',
    image: '',
    order: 0
  };

  const [formData, setFormData] = useState(emptyItem);

  const iconOptions = [
    'Building2', 'Landmark', 'HeartPulse', 'ShoppingCart', 'Truck', 'Factory',
    'GraduationCap', 'Building', 'Film', 'Zap', 'Home', 'Globe', 'Plane', 'Ship'
  ];

  useEffect(() => {
    loadMenuItems();
  }, []);

  const loadMenuItems = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/industry-menu?active_only=false`);
      const data = await response.json();
      setMenuItems(data);
    } catch (error) {
      toast.error('Failed to load industry menu');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      name,
      slug: prev.slug || generateSlug(name)
    }));
  };

  // Sub-item handlers
  const addSubItem = () => {
    setFormData(prev => ({
      ...prev,
      sub_items: [...prev.sub_items, { ...emptySubItem, id: `temp-${Date.now()}`, order: prev.sub_items.length }]
    }));
  };

  const updateSubItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      sub_items: prev.sub_items.map((item, i) => {
        if (i === index) {
          const updated = { ...item, [field]: value };
          if (field === 'name' && !item.slug) {
            updated.slug = generateSlug(value);
          }
          return updated;
        }
        return item;
      })
    }));
  };

  const removeSubItem = (index) => {
    setFormData(prev => ({
      ...prev,
      sub_items: prev.sub_items.filter((_, i) => i !== index)
    }));
  };

  // Case study handlers
  const addCaseStudy = () => {
    setFormData(prev => ({
      ...prev,
      case_studies: [...prev.case_studies, { ...emptyCaseStudy, id: `temp-${Date.now()}`, order: prev.case_studies.length }]
    }));
  };

  const updateCaseStudy = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      case_studies: prev.case_studies.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeCaseStudy = (index) => {
    setFormData(prev => ({
      ...prev,
      case_studies: prev.case_studies.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = editingId 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/cms/industry-menu/${editingId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/cms/industry-menu`;
      
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...formData,
          order: parseInt(formData.order) || 0
        })
      });

      if (!response.ok) throw new Error('Failed to save');

      toast.success(editingId ? 'Industry updated!' : 'Industry created!');
      setFormData(emptyItem);
      setEditingId(null);
      setShowAddForm(false);
      setActiveTab('basic');
      loadMenuItems();
    } catch (error) {
      toast.error('Failed to save industry');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setFormData({
      name: item.name,
      slug: item.slug,
      icon: item.icon || 'Building2',
      description: item.description || '',
      image: item.image || '',
      hero_title: item.hero_title || '',
      hero_subtitle: item.hero_subtitle || '',
      hero_image: item.hero_image || '',
      content: item.content || '',
      content_image: item.content_image || '',
      features: item.features || [],
      benefits: item.benefits || [],
      sub_items: item.sub_items || [],
      case_studies: item.case_studies || [],
      order: item.order,
      is_active: item.is_active
    });
    setEditingId(item.id);
    setShowAddForm(true);
    setActiveTab('basic');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this industry?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/industry-menu/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) throw new Error('Failed to delete');

      toast.success('Industry deleted!');
      loadMenuItems();
    } catch (error) {
      toast.error('Failed to delete industry');
    }
  };

  const handleCancel = () => {
    setFormData(emptyItem);
    setEditingId(null);
    setShowAddForm(false);
    setActiveTab('basic');
  };

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-[#320547]" size={48} />
      </div>
    );
  }

  const tabs = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'page', label: 'Page Content' },
    { id: 'solutions', label: `Solutions (${formData.sub_items.length})` },
    { id: 'casestudies', label: `Case Studies (${formData.case_studies.length})` },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Building2 size={24} className="text-[#95228A]" />
            Industry Menu Management
          </CardTitle>
          {!showAddForm && (
            <Button onClick={() => setShowAddForm(true)} className="bg-[#320547] hover:bg-[#95228A]">
              <Plus size={18} className="mr-1" /> Add Industry
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {/* Add/Edit Form */}
          {showAddForm && (
            <form onSubmit={handleSubmit} className="mb-8 bg-gray-50 rounded-xl overflow-hidden">
              {/* Form Header */}
              <div className="bg-[#320547] text-white p-4">
                <h3 className="font-semibold text-lg">
                  {editingId ? 'Edit Industry' : 'Add New Industry'}
                </h3>
                <p className="text-white/70 text-sm">Fill in the details below</p>
              </div>

              {/* Tabs */}
              <div className="flex border-b bg-white">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-6 py-3 text-sm font-medium transition-colors ${
                      activeTab === tab.id 
                        ? 'border-b-2 border-[#320547] text-[#320547]' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="p-6 space-y-4">
                {/* Basic Info Tab */}
                {activeTab === 'basic' && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Industry Name *</label>
                        <Input
                          name="name"
                          value={formData.name}
                          onChange={handleNameChange}
                          placeholder="e.g., Finance & Banking"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Slug *</label>
                        <Input
                          name="slug"
                          value={formData.slug}
                          onChange={handleChange}
                          placeholder="e.g., fintech"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Icon</label>
                        <select
                          name="icon"
                          value={formData.icon}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border rounded-md text-sm h-10"
                        >
                          {iconOptions.map(icon => (
                            <option key={icon} value={icon}>{icon}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Description (for menu & cards)</label>
                      <Textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Brief description shown in navigation and industry cards"
                        rows={2}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Display Order</label>
                        <Input
                          type="number"
                          name="order"
                          value={formData.order}
                          onChange={handleChange}
                          min="0"
                        />
                      </div>
                      <div className="flex items-center gap-2 pt-6">
                        <input
                          type="checkbox"
                          name="is_active"
                          checked={formData.is_active}
                          onChange={handleChange}
                          className="w-4 h-4"
                        />
                        <label className="text-sm font-medium">Active (visible in menu)</label>
                      </div>
                    </div>
                  </>
                )}

                {/* Page Content Tab */}
                {activeTab === 'page' && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Hero Title</label>
                        <Input
                          name="hero_title"
                          value={formData.hero_title}
                          onChange={handleChange}
                          placeholder="Main heading for the industry page"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Hero Image URL</label>
                        <Input
                          name="hero_image"
                          value={formData.hero_image}
                          onChange={handleChange}
                          placeholder="https://images.unsplash.com/..."
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Hero Subtitle</label>
                      <Textarea
                        name="hero_subtitle"
                        value={formData.hero_subtitle}
                        onChange={handleChange}
                        placeholder="Supporting text below the main heading"
                        rows={2}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Page Content</label>
                      <Textarea
                        name="content"
                        value={formData.content}
                        onChange={handleChange}
                        placeholder="Main content for the industry page (supports multiple paragraphs)"
                        rows={5}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Content Image URL</label>
                      <Input
                        name="content_image"
                        value={formData.content_image}
                        onChange={handleChange}
                        placeholder="https://images.unsplash.com/..."
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Key Features (one per line)</label>
                        <Textarea
                          value={formData.features?.join('\n') || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            features: e.target.value.split('\n').filter(f => f.trim())
                          }))}
                          placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
                          rows={4}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Benefits (one per line)</label>
                        <Textarea
                          value={formData.benefits?.join('\n') || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            benefits: e.target.value.split('\n').filter(b => b.trim())
                          }))}
                          placeholder="Benefit 1&#10;Benefit 2&#10;Benefit 3"
                          rows={4}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Solutions Tab */}
                {activeTab === 'solutions' && (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-sm text-gray-600">Add solutions/sub-pages for this industry</p>
                      <Button type="button" onClick={addSubItem} variant="outline" size="sm">
                        <Plus size={16} className="mr-1" /> Add Solution
                      </Button>
                    </div>

                    {formData.sub_items.length === 0 ? (
                      <div className="text-center py-8 bg-white rounded-lg border-2 border-dashed">
                        <Building2 size={40} className="mx-auto text-gray-300 mb-2" />
                        <p className="text-gray-500">No solutions yet. Click "Add Solution" to add one.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {formData.sub_items.map((subItem, index) => (
                          <div key={subItem.id || index} className="p-4 bg-white rounded-lg border">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <GripVertical size={18} className="text-gray-400" />
                                <span className="font-medium text-sm text-[#320547]">Solution {index + 1}</span>
                              </div>
                              <Button
                                type="button"
                                onClick={() => removeSubItem(index)}
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                              <Input
                                value={subItem.name}
                                onChange={(e) => updateSubItem(index, 'name', e.target.value)}
                                placeholder="Solution name"
                              />
                              <Input
                                value={subItem.slug}
                                onChange={(e) => updateSubItem(index, 'slug', e.target.value)}
                                placeholder="slug"
                              />
                              <Input
                                value={subItem.description || ''}
                                onChange={(e) => updateSubItem(index, 'description', e.target.value)}
                                placeholder="Brief description"
                              />
                            </div>
                            
                            <details className="bg-gray-50 rounded-lg p-3">
                              <summary className="cursor-pointer text-sm font-medium text-gray-600 hover:text-[#320547]">
                                Page Content (click to expand)
                              </summary>
                              <div className="mt-3 space-y-3">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <Input
                                    value={subItem.hero_title || ''}
                                    onChange={(e) => updateSubItem(index, 'hero_title', e.target.value)}
                                    placeholder="Hero Title"
                                  />
                                  <Input
                                    value={subItem.hero_image || ''}
                                    onChange={(e) => updateSubItem(index, 'hero_image', e.target.value)}
                                    placeholder="Hero Image URL"
                                  />
                                </div>
                                <Textarea
                                  value={subItem.content || ''}
                                  onChange={(e) => updateSubItem(index, 'content', e.target.value)}
                                  placeholder="Page Content"
                                  rows={3}
                                />
                              </div>
                            </details>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Case Studies Tab */}
                {activeTab === 'casestudies' && (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-sm text-gray-600">Add case studies to showcase your work in this industry</p>
                      <Button type="button" onClick={addCaseStudy} variant="outline" size="sm">
                        <Plus size={16} className="mr-1" /> Add Case Study
                      </Button>
                    </div>

                    {formData.case_studies.length === 0 ? (
                      <div className="text-center py-8 bg-white rounded-lg border-2 border-dashed">
                        <Briefcase size={40} className="mx-auto text-gray-300 mb-2" />
                        <p className="text-gray-500">No case studies yet. Click "Add Case Study" to add one.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {formData.case_studies.map((caseStudy, index) => (
                          <div key={caseStudy.id || index} className="p-4 bg-white rounded-lg border">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <Briefcase size={18} className="text-[#320547]" />
                                <span className="font-medium text-sm text-[#320547]">Case Study {index + 1}</span>
                              </div>
                              <Button
                                type="button"
                                onClick={() => removeCaseStudy(index)}
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                              <Input
                                value={caseStudy.client}
                                onChange={(e) => updateCaseStudy(index, 'client', e.target.value)}
                                placeholder="Client name *"
                                required
                              />
                              <Input
                                value={caseStudy.title}
                                onChange={(e) => updateCaseStudy(index, 'title', e.target.value)}
                                placeholder="Project title *"
                                required
                              />
                            </div>

                            <div className="mb-3">
                              <Input
                                value={caseStudy.image || ''}
                                onChange={(e) => updateCaseStudy(index, 'image', e.target.value)}
                                placeholder="Image URL (optional)"
                              />
                            </div>
                            
                            <div className="grid grid-cols-1 gap-3">
                              <Textarea
                                value={caseStudy.challenge || ''}
                                onChange={(e) => updateCaseStudy(index, 'challenge', e.target.value)}
                                placeholder="Challenge: What problem did the client face?"
                                rows={2}
                              />
                              <Textarea
                                value={caseStudy.solution || ''}
                                onChange={(e) => updateCaseStudy(index, 'solution', e.target.value)}
                                placeholder="Solution: How did you solve it?"
                                rows={2}
                              />
                              <Textarea
                                value={caseStudy.results || ''}
                                onChange={(e) => updateCaseStudy(index, 'results', e.target.value)}
                                placeholder="Results: What was the outcome? (e.g., 50% increase in efficiency)"
                                rows={2}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 p-6 bg-gray-100 border-t">
                <Button type="submit" disabled={saving} className="bg-[#320547] hover:bg-[#95228A]">
                  {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : <Save size={18} className="mr-2" />}
                  {editingId ? 'Update Industry' : 'Create Industry'}
                </Button>
                <Button type="button" variant="outline" onClick={handleCancel}>
                  <X size={18} className="mr-2" /> Cancel
                </Button>
              </div>
            </form>
          )}

          {/* Menu Items List */}
          {menuItems.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Building2 size={48} className="mx-auto mb-4 opacity-30" />
              <p>No industries yet. Add your first one!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {menuItems.map((item) => (
                <div 
                  key={item.id} 
                  className={`rounded-xl border ${item.is_active ? 'border-gray-200 bg-white' : 'border-red-200 bg-red-50/50'}`}
                >
                  <div className="flex items-center gap-4 p-4">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#320547] to-[#95228A] flex items-center justify-center text-white font-bold">
                      {item.name.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-bold text-gray-900">{item.name}</h4>
                        <span className="text-xs text-gray-400">/{item.slug}</span>
                        {!item.is_active && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded">Inactive</span>
                        )}
                      </div>
                      <p className="text-gray-500 text-sm line-clamp-1">{item.description || 'No description'}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-gray-400">{item.sub_items?.length || 0} solutions</span>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-xs text-gray-400">{item.case_studies?.length || 0} case studies</span>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-xs text-gray-400">Order: {item.order}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {(item.sub_items?.length > 0 || item.case_studies?.length > 0) && (
                        <Button size="sm" variant="ghost" onClick={() => toggleExpand(item.id)}>
                          {expandedItems[item.id] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => handleEdit(item)}>
                        <Edit2 size={14} className="mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(item.id)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Expanded View */}
                  {expandedItems[item.id] && (
                    <div className="border-t bg-gray-50 p-4 space-y-4">
                      {/* Solutions */}
                      {item.sub_items?.length > 0 && (
                        <div>
                          <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Solutions</h5>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                            {item.sub_items.map((subItem, idx) => (
                              <div key={subItem.id || idx} className="flex items-center gap-2 p-2 bg-white rounded border text-sm">
                                <span className="w-5 h-5 rounded bg-[#95228A]/10 text-[#95228A] flex items-center justify-center text-xs font-bold">
                                  {idx + 1}
                                </span>
                                <span className="font-medium truncate">{subItem.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Case Studies */}
                      {item.case_studies?.length > 0 && (
                        <div>
                          <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Case Studies</h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {item.case_studies.map((cs, idx) => (
                              <div key={cs.id || idx} className="p-3 bg-white rounded border">
                                <div className="flex items-center gap-2 mb-1">
                                  <Briefcase size={14} className="text-[#320547]" />
                                  <span className="font-medium text-sm">{cs.client}</span>
                                </div>
                                <p className="text-gray-600 text-sm">{cs.title}</p>
                                {cs.results && (
                                  <p className="text-green-600 text-xs mt-1 font-medium">{cs.results}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default IndustryMenuPanel;
